import React, { useEffect, useState } from 'react';
/* eslint-disable import/no-extraneous-dependencies */
import { StyleProvider } from '@ant-design/cssinjs';
import { DevtoolsProvider, DevtoolsPanel } from '@refinedev/devtools';
import { notificationProvider } from '@refinedev/antd';
import { Refine } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import routerBindings, {
  // DocumentTitleHandler,
  UnsavedChangesNotifier,
} from '@refinedev/react-router-v6';

import AppRoutes from 'core/routes/routes';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import ConfigStyle from 'core/constants/ConfigStyle';
import { useTranslation } from 'react-i18next';
import customDataProvider from 'providers/DataProvder';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import { REACT_APP_API_URL, REACT_APP_API_URL_ORG } from 'config';
import { useKeycloak } from '@react-keycloak/web';
import authProvider from 'providers/AuthProvider';
import Loading from 'components/loading/loading';
import { Images } from 'core/common/imagePath';

type I18nProviderType = {
  translate: (key: string, options?: Record<string, unknown>) => string;
  changeLocale: (lang: string) => Promise<void>;
  getLocale: () => string;
};

const Initiate = () => {
  // const customTitleHandler = () => 'Payprompt';
  const { filteredResources, setToken, setUserSessionId } = useTokenContext();
  const { t, i18n } = useTranslation();

  const i18nProvider: I18nProviderType = {
    translate: (key: string, options?: Record<string, unknown>) =>
      t(key, options),
    async changeLocale(lang: string): Promise<void> {
      await i18n.changeLanguage(lang);
    },
    getLocale: () => i18n.language,
  };

  const userDataProvider = customDataProvider(REACT_APP_API_URL);
  const organizationDataProvider = customDataProvider(REACT_APP_API_URL_ORG);

  const [keycloakReady, setKeycloakReady] = useState(false);

  const { keycloak, initialized } = useKeycloak();

  useEffect(() => {
    console.log('Keycloak initialization status:', {
      initialized,
      authenticated: keycloak?.authenticated,
    });

    if (initialized) {
      console.log('Keycloak initialized, proceeding...');
      setKeycloakReady(true);
    }
  }, [initialized, keycloak]); // Added keycloak to dependencies

  // Debugging logs
  console.log('Current render state:', {
    initialized,
    keycloakReady,
    keycloak: !!keycloak,
    authenticated: keycloak?.authenticated,
  });

  if (!keycloakReady) {
    console.log('Showing loading state');
    return <Loading />;
  }

  if (!keycloak?.authenticated) {
    console.log('User not authenticated');
    return <h1>User not authenticated</h1>;
  }

  console.log('Rendering main app');
  Spin.setDefaultIndicator(
    <img
      style={{ width: '100px', height: '100px' }}
      src={Images.loaderGif}
      alt="loading"
    />
  );

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <StyleProvider hashPriority="high" transformers={[]}>
          <ConfigProvider csp={{ nonce: 'osian9839980' }} {...ConfigStyle}>
            <DevtoolsProvider>
              <Refine
                dataProvider={{
                  default: userDataProvider,
                  organizationDataProvider,
                }}
                i18nProvider={i18nProvider}
                notificationProvider={notificationProvider}
                routerProvider={routerBindings}
                authProvider={authProvider(
                  keycloak,
                  setToken,
                  setUserSessionId
                )}
                resources={filteredResources}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  projectId: 'yFsPTU-0WFGC2-VwzawS',
                }}
              >
                <AppRoutes />
                <RefineKbar />
                <UnsavedChangesNotifier />
                {/* <DocumentTitleHandler handler={customTitleHandler} /> */}
              </Refine>
              <DevtoolsPanel />
            </DevtoolsProvider>
          </ConfigProvider>
        </StyleProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
};

export default Initiate;
